import { useCallback, useEffect, useMemo } from "react";

import { TokenPositionExpanded, WhirlpoolPositionExpanded } from "@bridgesplit/abf-sdk";
import {
    calculatePercentChange,
    formatNum,
    formatPercent,
    lamportsToUiAmount,
    Result,
    roundToDecimals
} from "@bridgesplit/utils";
import {
    tickIndexToPrice,
    priceToTickIndex,
    getNearestValidTickIndexFromTickIndex,
    Percentage,
    sqrtPriceX64ToPrice
} from "@orca-so/whirlpool-sdk";
import BN from "bn.js";
import Decimal from "decimal.js";
import {
    calculateHealthRatioFromLtv,
    calculateLoanHealth,
    getHealthStatus,
    getZcLedger,
    MAX_TICK_INDEX,
    MIN_TICK_INDEX
} from "@bridgesplit/abf-react";
import { HealthDetails } from "app/components/common";

import { useWhirlpoolContext, AUTO_SLIPPAGE_DECIMALS } from "./WhirlpoolContext";
import { PRICE_RANGE_DECIMALS } from "./common";
import { getPositionData, getPoolData } from "./orca";
import { useClmmRefinanceInfo } from "../clmm/util";

export function useInitPositionData() {
    const { positionData, setPositionData, whirlpoolPosition, setInitialWithdrawQuote } = useWhirlpoolContext();

    useEffect(() => {
        if (positionData !== undefined) return;
        (async () => {
            const data = await getPositionData(whirlpoolPosition);
            setPositionData(data?.positionData ?? null);
            setInitialWithdrawQuote(data?.quote);
        })();
    }, [positionData, setInitialWithdrawQuote, setPositionData, whirlpoolPosition]);
}

export function useInitPoolData() {
    const { poolData, setPoolData, whirlpoolPosition } = useWhirlpoolContext();

    useEffect(() => {
        if (poolData !== undefined) return;
        (async () => {
            const data = await getPoolData(whirlpoolPosition.whirlpoolAddress);
            setPoolData(data);
        })();
    }, [poolData, setPoolData, whirlpoolPosition]);
}

export function isMaxTickIndex(tickIndex: number, tickSpacing: number) {
    const maxTickIndex = getNearestValidTickIndexFromTickIndex(MAX_TICK_INDEX, tickSpacing);
    return tickIndex === maxTickIndex;
}

export function isMinTickIndex(tickIndex: number, tickSpacing: number) {
    const minTickIndex = getNearestValidTickIndexFromTickIndex(MIN_TICK_INDEX, tickSpacing);
    return tickIndex === minTickIndex;
}

export function flipPriceIfNeeded(price: number | undefined, isFlippedPrice: boolean) {
    if (!price) return price;
    return isFlippedPrice ? roundToDecimals(1 / price, PRICE_RANGE_DECIMALS) : price;
}

export function useWhirlpoolFormatPrice() {
    const { isFlippedPrice, whirlpoolPosition } = useWhirlpoolContext();
    return useCallback(
        (tickIndex: number | undefined, decimals = PRICE_RANGE_DECIMALS) => {
            if (tickIndex === undefined) return undefined;

            const price = getPriceFromTick(tickIndex, whirlpoolPosition);
            const flippedPrice = flipPriceIfNeeded(price, isFlippedPrice);
            return formatNum(flippedPrice, { customDecimals: decimals });
        },
        [isFlippedPrice, whirlpoolPosition]
    );
}

export function useFormatWhirlpoolPercentChangeFromCurrent() {
    const { whirlpoolPosition, isFlippedPrice } = useWhirlpoolContext();
    return useCallback(
        (tickIndex: number | undefined, currentTickIndex: number | undefined) => {
            if (tickIndex === undefined || currentTickIndex === undefined) return undefined;

            const price = getPriceFromTick(tickIndex, whirlpoolPosition);
            const currentPrice = getPriceFromTick(currentTickIndex, whirlpoolPosition);

            let percentChange = calculatePercentChange(currentPrice, price);
            if (isFlippedPrice) {
                percentChange = -percentChange;
            }
            let formatted = formatPercent(percentChange);
            if (percentChange > 0) formatted = `+${formatted}`;
            return formatted;
        },
        [isFlippedPrice, whirlpoolPosition]
    );
}

export const getPriceFromTick = (tickIndex: number, whirlpoolPosition: WhirlpoolPositionExpanded) => {
    try {
        if (isMaxTickIndex(tickIndex, whirlpoolPosition.whirlpool.tickSpacing)) return Infinity;
        if (isMinTickIndex(tickIndex, whirlpoolPosition.whirlpool.tickSpacing)) return 0;
        return tickIndexToPrice(
            tickIndex,
            whirlpoolPosition.tokenA.decimals,
            whirlpoolPosition.tokenB.decimals
        ).toNumber();
    } catch (error) {
        Result.err(error);
        return 0;
    }
};

export function getTickFromPrice(price: number, whirlpoolPosition: WhirlpoolPositionExpanded) {
    try {
        if (price === Infinity) return MAX_TICK_INDEX;
        if (price === 0) return MIN_TICK_INDEX;
        const roundedTick = priceToTickIndex(
            new Decimal(price),
            whirlpoolPosition.tokenA.decimals,
            whirlpoolPosition.tokenB.decimals
        );
        return getNearestValidTickIndexFromTickIndex(roundedTick, whirlpoolPosition.whirlpool.tickSpacing);
    } catch (error) {
        Result.err(error);
        return MIN_TICK_INDEX;
    }
}

export const getTokenUsdPrice = (token: TokenPositionExpanded, amount: BN) =>
    lamportsToUiAmount(amount.toNumber(), token.decimals) * token.usdPrice;

// Calculate denominator (1/decimalValue)
export function getSlippageTolerance(slippagePercent: number) {
    if (slippagePercent === 0) return new Percentage(new BN(0), new BN(1));
    const denominator = Math.round(1 / slippagePercent);
    const percent = new Percentage(new BN(1), new BN(denominator));
    return percent;
}

export function useWhirlpoolLtv(positionUsdValue: number | undefined) {
    const {
        loanExpanded,
        slippageController: { slippagePercentDecimals }
    } = useWhirlpoolContext();

    const firstLedger = getZcLedger(loanExpanded);
    const { refinanceInfo } = useClmmRefinanceInfo(loanExpanded);

    const totalDebtUsd = (loanExpanded?.principalUsdPrice ?? 0) * (firstLedger?.ledgerDebt.total ?? 0);
    const ltv = positionUsdValue !== undefined ? totalDebtUsd / positionUsdValue : 1;

    const maxLtvWithSlippage = refinanceInfo?.ltv
        ? refinanceInfo.ltv * (1 + (slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS))
        : 1;
    const insufficientCollateral = refinanceInfo && ltv > maxLtvWithSlippage;

    const { health, loanHealthStatus, ltv: loanLtv } = calculateLoanHealth(loanExpanded);

    const originalHealth: HealthDetails = {
        health,
        loanHealthStatus,
        ltv: loanLtv
    };

    const newHealth: HealthDetails = {
        health: calculateHealthRatioFromLtv(ltv, refinanceInfo?.liquidationThreshold),
        loanHealthStatus: getHealthStatus(ltv, refinanceInfo?.ltv, refinanceInfo?.liquidationThreshold),
        ltv
    };

    return {
        ltv,
        refinanceInfo,
        insufficientCollateral,
        isLoading: positionUsdValue === undefined,
        originalHealth,
        newHealth
    };
}

export function useInitialWhirlpoolRatio() {
    const { whirlpoolPosition, poolData, positionData } = useWhirlpoolContext();
    return useMemo(() => {
        if (!poolData || !positionData) return undefined;
        return calculateDepositRatios(
            poolData?.sqrtPrice,
            whirlpoolPosition.tokenA.metadata.decimals,
            whirlpoolPosition.tokenB.metadata.decimals,
            positionData.tickLowerIndex,
            positionData.tickUpperIndex
        );
    }, [poolData, whirlpoolPosition, positionData]);
}

//  https://github.com/everlastingsong/solsandbox/blob/23cc81160e5d66376da7464fbaf9645d4d2abb76/orca/whirlpool/whirlpools_sdk/78a_deposit_ratio.ts
export function calculateDepositRatios(
    sqrtPrice: BN,
    tokenADecimals: number,
    tokenBDecimals: number,
    lowerTickIndex: number,
    upperTickIndex: number
): { ratioA: number; ratioB: number } | undefined {
    try {
        const price = sqrtPriceX64ToPrice(sqrtPrice, tokenADecimals, tokenBDecimals).toNumber();

        const lowerSqrtPrice = Math.sqrt(tickIndexToPrice(lowerTickIndex, tokenADecimals, tokenBDecimals).toNumber());
        const upperSqrtPrice = Math.sqrt(tickIndexToPrice(upperTickIndex, tokenADecimals, tokenBDecimals).toNumber());
        const clampedCurrentSqrtPrice = Math.min(Math.max(lowerSqrtPrice, Math.sqrt(price)), upperSqrtPrice);

        const depositA = 1 / clampedCurrentSqrtPrice - 1 / upperSqrtPrice;
        const depositB = clampedCurrentSqrtPrice - lowerSqrtPrice;

        const depositAValueInB = depositA * price;
        const depositBValueInB = depositB;
        const totalValueInB = depositAValueInB + depositBValueInB;

        const ratioA = depositAValueInB / totalValueInB;
        const ratioB = depositBValueInB / totalValueInB;

        return { ratioA, ratioB };
    } catch (error) {
        return undefined;
    }
}
